import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread, parseIntOrDefault } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { buildSectionSelector } from '@theme/section-renderer';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';
import { resolveVariantId } from '@theme/variant-resolution';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

const CART_REQUEST_TIMEOUT = 10000;
const DELAYED_CART_RECONCILIATION_MS = 1500;

/** @type {number | undefined} */
let delayedAddReconciliationTimer;
let activeCartAddRequests = 0;
let cartReconciliationNeeded = false;
let delayedAddReconciliationNeeded = false;
let cartReconciliationInFlight = false;
/** @type {{productId: string | undefined, errorMessage: string} | undefined} */
let cartReconciliationFeedback;

function getUnconfirmedCartChangeMessage() {
  return (
    Theme.translations?.cart_change_unconfirmed ||
    "We couldn't confirm that cart change. Review your cart before trying again."
  );
}

/**
 * @param {string} url
 * @param {RequestInit} options
 * @param {string} requestName
 */
async function fetchCartJSON(url, options, requestName) {
  const abortController = new AbortController();
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    abortController.abort();
  }, CART_REQUEST_TIMEOUT);

  try {
    const response = await fetch(url, { ...options, signal: abortController.signal });
    const responseText = await response.text();
    let data;

    try {
      data = JSON.parse(responseText);
    } catch {
      throw new Error(`${requestName} returned invalid JSON (${response.status})`);
    }

    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      throw new Error(`${requestName} returned an invalid response`);
    }

    return { data, response };
  } catch (error) {
    if (timedOut) throw new Error(`${requestName} timed out`);
    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

/** @param {any} cart */
function validateCart(cart) {
  if (
    !Array.isArray(cart?.items) ||
    !Number.isFinite(cart?.item_count) ||
    !Number.isFinite(cart?.total_price) ||
    typeof cart?.currency !== 'string' ||
    !cart.items.every(
      /** @param {any} item */
      (item) =>
        (typeof item?.variant_id === 'number' || typeof item?.variant_id === 'string') &&
        Number.isFinite(item?.quantity)
    )
  ) {
    throw new Error('Cart response was missing required data');
  }

  return cart;
}

/** @param {any} response */
function validateSingleAddResponse(response) {
  if ((typeof response?.id !== 'number' && typeof response?.id !== 'string') || !Number.isFinite(response?.quantity)) {
    throw new Error('Add to cart returned an invalid response');
  }
  return response;
}

/** @param {any} response */
function validateBatchAddResponse(response) {
  if (
    !Array.isArray(response?.items) ||
    response.items.length === 0 ||
    !response.items.every(
      /** @param {any} item */
      (item) =>
        (typeof item?.id === 'number' || typeof item?.id === 'string') && Number.isFinite(item?.quantity)
    )
  ) {
    throw new Error('Add to cart returned an invalid response');
  }
  return response;
}

/**
 * Returns a customer-safe Shopify validation message. Other error-shaped
 * responses are treated as ambiguous because a proxy may have generated the
 * response after forwarding the mutation.
 * @param {any} data
 * @param {Response} response
 */
function getExplicitAddErrorMessage(data, response) {
  const bodyStatus = Number(data?.status);
  const isShopifyValidationResponse =
    (bodyStatus === 400 || bodyStatus === 422) && response.status === bodyStatus;
  const message =
    (typeof data?.description === 'string' && data.description.trim()) ||
    (typeof data?.message === 'string' && data.message.trim()) ||
    (typeof data?.errors === 'string' && data.errors.trim()) ||
    '';

  if (isShopifyValidationResponse && message) return message;
  if (!response.ok || data?.status) {
    throw new Error(
      `Add to cart returned an untrusted error response (${response.status} ${response.statusText})`
    );
  }

  return null;
}

/**
 * @param {any} sections
 * @param {string[]} sectionIds
 * @param {number} [expectedItemCount]
 */
function inspectCartSections(sections, sectionIds, expectedItemCount) {
  /** @type {Record<string, string>} */
  const validSections = {};
  let itemCount = null;

  for (const sectionId of sectionIds) {
    const html = sections?.[sectionId];
    if (typeof html !== 'string' || html.trim() === '') continue;

    const responseDocument = new DOMParser().parseFromString(html, 'text/html');
    const section = responseDocument.getElementById(buildSectionSelector(sectionId));
    if (!section) continue;

    const count = Number.parseInt(section.querySelector('[ref="cartItemCount"]')?.textContent ?? '', 10);
    if (expectedItemCount !== undefined && count !== expectedItemCount) continue;
    if (itemCount !== null && Number.isFinite(count) && count !== itemCount) continue;

    validSections[sectionId] = html;
    if (Number.isFinite(count) && count >= 0) itemCount = count;
  }

  return {
    sections: Object.keys(validSections).length > 0 ? validSections : undefined,
    itemCount,
  };
}

function getCurrentCartItemCount() {
  const count = Number.parseInt(document.querySelector('[ref="cartItemCount"]')?.textContent ?? '', 10);
  return Number.isFinite(count) && count >= 0 ? count : 0;
}

function beginCartAdd() {
  const cartItemsComponent = document.querySelector('cart-items-component');
  if (
    activeCartAddRequests > 0 ||
    cartReconciliationInFlight ||
    (cartItemsComponent && !customElements.get('cart-items-component'))
  ) {
    cartReconciliationNeeded = true;
  }

  activeCartAddRequests += 1;
  if (delayedAddReconciliationTimer !== undefined) {
    clearTimeout(delayedAddReconciliationTimer);
    delayedAddReconciliationTimer = undefined;
  }
}

/**
 * Fetches the current cart through the live cart component when possible so
 * concurrent callers share its trailing-edge contract.
 */
async function fetchCurrentCart() {
  /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
  const cartItemsComponent = document.querySelector('cart-items-component');

  if (
    cartItemsComponent &&
    'fetchCartData' in cartItemsComponent &&
    typeof cartItemsComponent.fetchCartData === 'function'
  ) {
    return cartItemsComponent.fetchCartData();
  }

  const { data, response } = await fetchCartJSON(
    `${Theme.routes.cart_url}.json`,
    {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    },
    'Cart refresh'
  );
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
  return validateCart(data);
}

/**
 * @param {any} cart
 * @param {{productId: string | undefined, errorMessage: string} | undefined} [feedback]
 */
async function applyCurrentCart(cart, feedback) {
  const cartItemsComponents = Array.from(document.querySelectorAll('cart-items-component'));
  const readyComponents = cartItemsComponents.filter(
    (cartItemsComponent) =>
      'reconcileCartData' in cartItemsComponent && typeof cartItemsComponent.reconcileCartData === 'function'
  );

  if (readyComponents.length !== cartItemsComponents.length) {
    throw new Error('Cart items component was not ready for reconciliation');
  }
  if (readyComponents.length > 0) {
    await Promise.all(readyComponents.map((component) => component.reconcileCartData(cart, feedback)));
    return;
  }

  for (const cartIcon of document.querySelectorAll('cart-icon')) {
    if ('renderCartBubble' in cartIcon && typeof cartIcon.renderCartBubble === 'function') {
      void cartIcon.renderCartBubble(cart.item_count, false);
    }
  }
  const liveRegion = document.querySelector('[data-testid="cart-count-live-region"]');
  const message = `${Theme.translations.cart_count}: ${cart.item_count}`;
  if (liveRegion && liveRegion.textContent !== message) liveRegion.textContent = message;
}

/**
 * Runs only repair reads requested by overlapping Adds, ambiguity, or a failed
 * per-Add reconciliation. Normal Adds reconcile through their event promise.
 * @param {boolean} [isDelayed=false]
 */
function runPendingAddReconciliation(isDelayed = false) {
  if (activeCartAddRequests > 0 || cartReconciliationInFlight) return;
  if (!cartReconciliationNeeded) {
    if (delayedAddReconciliationNeeded && delayedAddReconciliationTimer === undefined) {
      delayedAddReconciliationTimer = setTimeout(() => {
        delayedAddReconciliationTimer = undefined;
        if (activeCartAddRequests > 0 || cartReconciliationInFlight) return;
        delayedAddReconciliationNeeded = false;
        cartReconciliationNeeded = true;
        runPendingAddReconciliation(true);
      }, DELAYED_CART_RECONCILIATION_MS);
    } else if (!delayedAddReconciliationNeeded) {
      cartReconciliationFeedback = undefined;
    }
    return;
  }

  cartReconciliationNeeded = false;
  const feedback = cartReconciliationFeedback;
  cartReconciliationInFlight = true;
  void fetchCurrentCart()
    .then((cart) => applyCurrentCart(cart, feedback))
    .catch((error) => {
      if (!isDelayed) delayedAddReconciliationNeeded = true;
      if (error?.name !== 'AbortError') {
        console.warn('[product-form] Could not complete cart reconciliation:', error);
      }
    })
    .finally(() => {
      cartReconciliationInFlight = false;
      runPendingAddReconciliation();
    });
}

/**
 * @param {boolean} outcomeAmbiguous
 * @param {boolean} reconciliationFailed
 * @param {{productId: string | undefined, errorMessage: string} | undefined} [feedback]
 */
function finishCartAdd(outcomeAmbiguous, reconciliationFailed, feedback) {
  activeCartAddRequests = Math.max(0, activeCartAddRequests - 1);
  if (reconciliationFailed) cartReconciliationNeeded = true;
  if (outcomeAmbiguous) delayedAddReconciliationNeeded = true;
  if (feedback) cartReconciliationFeedback = feedback;

  // Give a queued Add a chance to start before deciding that the mutation
  // burst is finished. This avoids treating a serialized queue as overlap.
  queueMicrotask(() => runPendingAddReconciliation());
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number | string}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @typedef {object} QueuedAddToCartItem
 * @property {number} quantity - The quantity captured when Add was clicked.
 * @property {number} generation - The variant-change generation active when Add was clicked.
 * @property {string | null} intendedVariantId - The selected option's data-variant-id, when present.
 * @property {Promise<unknown> | null} pendingVariantChange - The server-side section fetch for the clicked selection.
 * @property {string | null} variantResolutionUrl - A section-rendering URL that resolves the clicked selection.
 * @property {string | null} variantPickerTagName - The picker element expected in the resolution response.
 *
 * @typedef {object} QuantityConstraints
 * @property {string} min
 * @property {string | null} max
 * @property {string} step
 * @property {string | null} cartQuantity
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #addInFlight = false;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /**
   * Adds queued while a variant change is in flight. Each entry captures the selection state and
   * generation active when Add was clicked, then resolves that selection at drain time.
   * @type {QueuedAddToCartItem[]}
   */
  #addToCartQueue = [];

  /**
   * Resolved queued Adds waiting for the current cart mutation to settle.
   * @type {Array<{variantId: string, quantity: number}>}
   */
  #pendingBatchAddItems = [];

  /** @type {number} */
  #activeAddGeneration = -1;

  /** @type {number} */
  #lastQueuedAddGeneration = -1;

  /**
   * The in-flight variant-change section fetch promise. The queue drain awaits this before
   * reading the resolved variant id.
   * @type {Promise<unknown> | null}
   */
  #pendingVariantChange = null;

  connectedCallback() {
    super.connectedCallback();

    // Quick Add can reconnect the same form after moving its responsive layout.
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    if (this.#timeout) clearTimeout(this.#timeout);
  }

  #getVariantIdInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'))?.value;
  }

  async #refreshCart() {
    return fetchCurrentCart();
  }

  /** @param {NodeListOf<AddToCartComponent>} addToCartContainers */
  #beginAdd(addToCartContainers) {
    this.#addInFlight = true;
    this.setAttribute('aria-busy', 'true');

    return Array.from(addToCartContainers, (container) => {
      const button = container.refs.addToCartButton;
      const wasDisabled = Boolean(button?.disabled);
      const wasFocused = button === document.activeElement;
      button?.setAttribute('data-cart-add-disabled', 'true');
      if (button) button.disabled = true;
      return { button, wasDisabled, wasFocused };
    });
  }

  /** @param {Array<{button: HTMLButtonElement | undefined, wasDisabled: boolean, wasFocused: boolean}>} buttonStates */
  #finishAdd(buttonStates) {
    const validation = this.refs.quantitySelector?.canAddToCart?.();
    const reachedQuantityLimit = validation ? !validation.canAdd : false;
    /** @type {HTMLButtonElement | undefined} */
    let focusTarget;

    for (const { button, wasDisabled, wasFocused } of buttonStates) {
      if (!button?.isConnected || button.getAttribute('data-cart-add-disabled') !== 'true') continue;
      button.removeAttribute('data-cart-add-disabled');
      button.disabled = wasDisabled || reachedQuantityLimit;
      if (wasFocused && !button.disabled) focusTarget = button;
    }
    this.#addInFlight = false;
    this.removeAttribute('aria-busy');

    if (focusTarget && (document.activeElement === document.body || document.activeElement === null)) {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch {
        focusTarget.focus();
      }
    }
  }

  /** @param {string} message */
  #showAddError(message) {
    const { addToCartTextError } = this.refs;
    if (addToCartTextError) {
      addToCartTextError.classList.remove('hidden');
      const textNode = addToCartTextError.childNodes[2];
      if (textNode) {
        textNode.textContent = message;
      } else {
        addToCartTextError.appendChild(document.createTextNode(message));
      }
    }

    this.#setLiveRegionText(message);
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      addToCartTextError?.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /** @param {string} message */
  #announceAddSuccess(message) {
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#setLiveRegionText(message);
    this.#timeout = setTimeout(() => {
      this.#timeout = undefined;
      this.#clearLiveRegionText();
    }, SUCCESS_MESSAGE_DISPLAY_DURATION);
  }

  /** @param {string} errorMessage */
  #reportAddServiceError(errorMessage) {
    this.#showAddError(errorMessage);
    this.dispatchEvent(new CartErrorEvent({ error: errorMessage, code: 'SERVICE_UNAVAILABLE' }));
    return { productId: this.dataset.productId, errorMessage };
  }

  /**
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferredEventPromise
   * @param {Object} result
   * @param {any | null} result.cart
   * @param {number} result.itemCount
   * @param {boolean} result.didError
   * @param {Record<string, string> | undefined} [result.sections]
   * @param {boolean | undefined} [result.cartChanged]
   * @param {boolean | undefined} [result.outcomeAmbiguous]
   * @param {string | undefined} [result.errorMessage]
   */
  #resolveAddEvent(
    deferredEventPromise,
    { cart, itemCount, didError, sections, cartChanged, outcomeAmbiguous, errorMessage }
  ) {
    deferredEventPromise.resolve({
      cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : null,
      detail: {
        items: cart?.items,
        source: 'product-form-component',
        sourceId: this.id.toString(),
        itemCount,
        productId: this.dataset.productId,
        sections,
        didError,
        cartChanged,
        outcomeAmbiguous,
        errorMessage,
      },
    });
  }

  /**
   * Settles a confirmed mutation from one authoritative cart read. Bundled
   * sections are used only when their count matches that read.
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferredEventPromise
   * @param {any} addResponse
   * @param {string[]} sectionIds
   * @param {number} previousItemCount
   * @returns {Promise<{productId: string | undefined, errorMessage: string} | undefined>}
   */
  async #settleConfirmedAdd(deferredEventPromise, addResponse, sectionIds, previousItemCount) {
    try {
      const cart = await this.#refreshCart();
      const inspectedSections = inspectCartSections(addResponse.sections, sectionIds, cart.item_count);
      this.#resolveAddEvent(deferredEventPromise, {
        cart,
        itemCount: cart.item_count,
        didError: false,
        sections: inspectedSections.sections,
      });
      if (this.#getVariantIdInput()) this.#updateCartQuantity(cart);
      return;
    } catch (error) {
      console.warn('[product-form] Could not verify a confirmed add with the cart endpoint:', error);
    }

    const errorMessage = 'The cart was updated, but the latest cart view could not be loaded';
    const feedback = this.#reportAddServiceError(errorMessage);
    const inspectedSections = inspectCartSections(addResponse.sections, sectionIds);
    this.#resolveAddEvent(deferredEventPromise, {
      cart: null,
      itemCount: inspectedSections.itemCount ?? previousItemCount,
      didError: true,
      sections: inspectedSections.sections,
      cartChanged: true,
      outcomeAmbiguous: false,
      errorMessage,
    });
    return feedback;
  }

  /**
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferredEventPromise
   * @param {number} previousItemCount
   * @param {boolean} outcomeAmbiguous
   * @param {string} errorMessage
   * @returns {Promise<boolean>}
   */
  async #settleFailedAddFromCart(
    deferredEventPromise,
    previousItemCount,
    outcomeAmbiguous,
    errorMessage
  ) {
    try {
      const cart = await this.#refreshCart();
      this.#resolveAddEvent(deferredEventPromise, {
        cart,
        itemCount: cart.item_count,
        didError: true,
        cartChanged: cart.item_count !== previousItemCount,
        outcomeAmbiguous,
        errorMessage,
      });
      if (this.#getVariantIdInput()) this.#updateCartQuantity(cart);
      return true;
    } catch (reconciliationError) {
      console.error('[product-form] Could not reconcile the cart after an add failure:', reconciliationError);
      this.#resolveAddEvent(deferredEventPromise, {
        cart: null,
        itemCount: previousItemCount,
        didError: true,
        cartChanged: false,
        outcomeAmbiguous,
        errorMessage,
      });
      return false;
    }
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();
    if (!variantIdInput) return;

    // Discounts such as Buy X Get Y can split one variant across several cart lines.
    let cartQty = 0;
    for (const item of cart.items) {
      if (item.variant_id.toString() === variantIdInput.toString()) cartQty += item.quantity;
    }

    // Use public API to update quantity selector
    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */ (
        this.querySelector('quantity-selector-component')
      );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        // Skip if this event came from this component
        if (detail?.sourceId === this.id || detail?.source === 'product-form-component') return;

        if (detail?.items) {
          this.#updateCartQuantity(/** @type {Cart} */ ({ items: detail.items }));
        } else {
          this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-form] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress || this.#addInFlight) {
      if (
        this.#variantChangeGeneration === this.#lastQueuedAddGeneration ||
        (this.#addInFlight && this.#variantChangeGeneration === this.#activeAddGeneration)
      ) {
        return;
      }

      this.#lastQueuedAddGeneration = this.#variantChangeGeneration;
      this.#addToCartQueue.push(this.#createQueuedAddToCartItem());
      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      if (!this.#variantChangeInProgress) void this.#drainAddToCartQueue();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /** @returns {QueuedAddToCartItem} */
  #createQueuedAddToCartItem() {
    const picker = this.#getVariantPicker();
    const selectedOption = picker?.selectedOption;

    return {
      quantity: this.#getQuantity(),
      generation: this.#variantChangeGeneration,
      intendedVariantId: selectedOption?.dataset.variantId ?? null,
      pendingVariantChange: this.#pendingVariantChange,
      variantResolutionUrl: picker && selectedOption ? picker.buildRequestUrl(selectedOption) : null,
      variantPickerTagName: picker?.tagName.toLowerCase() ?? null,
    };
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    if (this.#addInFlight) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length > 0) {
      formData.set('sections', cartItemComponentsSectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const previousItemCount = getCurrentCartItemCount();
    this.#activeAddGeneration = this.#variantChangeGeneration;
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    const fetchCfg = fetchConfig('javascript', { body: formData });
    this.#addInFlight = true;
    this.setAttribute('aria-busy', 'true');
    let buttonStates = [];

    void (async () => {
      let mutationConfirmed = false;
      let outcomeAmbiguous = false;
      let reconciliationFailed = false;
      let cartAddStarted = false;
      /** @type {{productId: string | undefined, errorMessage: string} | undefined} */
      let reconciliationFeedback;
      try {
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [
              {
                merchandiseId: /** @type {string} */ (formData.get('id')),
                quantity: itemCount,
              },
            ],
            promise: deferredEventPromise.promise,
          })
        );
        buttonStates = this.#beginAdd(allAddToCartContainers);
        beginCartAdd();
        cartAddStarted = true;

        const { data: response, response: httpResponse } = await fetchCartJSON(
          Theme.routes.cart_add_url,
          {
            ...fetchCfg,
            cache: 'no-store',
            credentials: 'same-origin',
            headers: {
              ...fetchCfg.headers,
              Accept: 'application/json',
            },
          },
          'Add to cart'
        );

        const explicitErrorMessage = getExplicitAddErrorMessage(response, httpResponse);
        if (explicitErrorMessage) {
          reconciliationFeedback = {
            productId: this.dataset.productId,
            errorMessage: explicitErrorMessage,
          };
          this.dispatchEvent(
            new CartErrorEvent({
              error: explicitErrorMessage,
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          this.#showAddError(explicitErrorMessage);
          reconciliationFailed = !(await this.#settleFailedAddFromCart(
            deferredEventPromise,
            previousItemCount,
            false,
            explicitErrorMessage
          ));
          return;
        }

        validateSingleAddResponse(response);
        mutationConfirmed = true;
        if (!formData.get('id')) throw new Error('Form ID is required');

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#announceAddSuccess(addedText);
        }

        const confirmedAddFeedback = await this.#settleConfirmedAdd(
          deferredEventPromise,
          response,
          cartItemComponentsSectionIds,
          previousItemCount
        );
        if (confirmedAddFeedback) {
          reconciliationFailed = true;
          reconciliationFeedback = confirmedAddFeedback;
        }
      } catch (error) {
        console.error(error);
        const errorMessage = mutationConfirmed
          ? String(error?.message || 'The cart was updated, but the latest cart view could not be loaded')
          : getUnconfirmedCartChangeMessage();
        reconciliationFeedback = this.#reportAddServiceError(errorMessage);
        outcomeAmbiguous = !mutationConfirmed;
        reconciliationFailed = !(await this.#settleFailedAddFromCart(
          deferredEventPromise,
          previousItemCount,
          !mutationConfirmed,
          errorMessage
        ));
      } finally {
        this.#finishAdd(buttonStates);
        if (cartAddStarted) {
          finishCartAdd(outcomeAmbiguous, reconciliationFailed, reconciliationFeedback);
        }
        this.#flushPendingBatchAdd();
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      }
    })();
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;
    if (this.#addInFlight) {
      this.#pendingBatchAddItems.push(...items);
      return;
    }

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const previousItemCount = getCurrentCartItemCount();
    this.#activeAddGeneration = this.#variantChangeGeneration;
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      ...(cartItemComponentsSectionIds.length > 0
        ? {
            sections: cartItemComponentsSectionIds.join(','),
            sections_url: window.location.pathname,
          }
        : {}),
    };
    this.#addInFlight = true;
    this.setAttribute('aria-busy', 'true');
    let buttonStates = [];

    void (async () => {
      let mutationConfirmed = false;
      let outcomeAmbiguous = false;
      let reconciliationFailed = false;
      let cartAddStarted = false;
      /** @type {{productId: string | undefined, errorMessage: string} | undefined} */
      let reconciliationFeedback;
      try {
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((item) => ({
              merchandiseId: item.variantId,
              quantity: item.quantity,
            })),
            promise: deferredEventPromise.promise,
          })
        );
        buttonStates = this.#beginAdd(allAddToCartContainers);
        beginCartAdd();
        cartAddStarted = true;

        const { data: response, response: httpResponse } = await fetchCartJSON(
          Theme.routes.cart_add_url,
          {
            method: 'POST',
            cache: 'no-store',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(payload),
          },
          'Add to cart'
        );

        const explicitErrorMessage = getExplicitAddErrorMessage(response, httpResponse);
        if (explicitErrorMessage) {
          reconciliationFeedback = {
            productId: this.dataset.productId,
            errorMessage: explicitErrorMessage,
          };
          this.dispatchEvent(
            new CartErrorEvent({
              error: explicitErrorMessage,
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );

          this.#showAddError(explicitErrorMessage);
          reconciliationFailed = !(await this.#settleFailedAddFromCart(
            deferredEventPromise,
            previousItemCount,
            false,
            explicitErrorMessage
          ));
          return;
        }

        validateBatchAddResponse(response);
        mutationConfirmed = true;
        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#announceAddSuccess(addedText);
        }

        const confirmedAddFeedback = await this.#settleConfirmedAdd(
          deferredEventPromise,
          response,
          cartItemComponentsSectionIds,
          previousItemCount
        );
        if (confirmedAddFeedback) {
          reconciliationFailed = true;
          reconciliationFeedback = confirmedAddFeedback;
        }
      } catch (error) {
        console.error(error);
        const errorMessage = mutationConfirmed
          ? String(error?.message || 'The cart was updated, but the latest cart view could not be loaded')
          : getUnconfirmedCartChangeMessage();
        reconciliationFeedback = this.#reportAddServiceError(errorMessage);
        outcomeAmbiguous = !mutationConfirmed;
        reconciliationFailed = !(await this.#settleFailedAddFromCart(
          deferredEventPromise,
          previousItemCount,
          !mutationConfirmed,
          errorMessage
        ));
      } finally {
        this.#finishAdd(buttonStates);
        if (cartAddStarted) {
          finishCartAdd(outcomeAmbiguous, reconciliationFailed, reconciliationFeedback);
        }
        this.#flushPendingBatchAdd();
      }
    })();
  }

  #flushPendingBatchAdd() {
    if (this.#addInFlight || this.#pendingBatchAddItems.length === 0) return;

    const items = this.#pendingBatchAddItems;
    this.#pendingBatchAddItems = [];
    this.#processBatchAddToCart(items);
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    // Skip events from product-cards when this form is at the section level
    const sourceCard = /** @type {Element | null} */ (event.target)?.closest('product-card');
    if (sourceCard && !sourceCard.contains(this)) return;

    // Track generation to prevent a stale (aborted) call from clearing the flag
    // while a newer variant selection is still pending.
    const generation = ++this.#variantChangeGeneration;
    this.#variantChangeInProgress = true;
    // Hold the in-flight section-fetch promise so the queue drain can await it before reading the
    // resolved variant id.
    this.#pendingVariantChange = event.promise;

    try {
      const { detail } = await event.promise;
      if (!detail?.html) return;

      const { html, newProduct, productId, resource } = detail;

      // Update product context if new product loaded (combined listing)
      if (newProduct) {
        this.dataset.productId = newProduct.id;
      } else if (productId && productId !== this.dataset.productId) {
        return;
      }

      if (generation === this.#variantChangeGeneration) {
        if (this.#timeout) clearTimeout(this.#timeout);
        this.refs.addToCartTextError?.classList.add('hidden');
        this.#clearLiveRegionText();
      }

      const { variantId } = this.refs;
      variantId.value = resource?.id ?? '';

      const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } =
        this.refs;
      const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

      // Update state and text for add-to-cart button
      if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

      // Update the button state
      if (resource == null || resource.available == false) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      if (newAddToCartButton && currentAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }

      if (acceleratedCheckoutButtonContainer) {
        if (resource == null || resource.available == false) {
          acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        } else {
          acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
        }
      }

      // Set the data attribute for the product variant media if it exists
      if (resource) {
        const productVariantMedia = resource.featured_media?.preview_image?.src;
        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia + '&width=100'
          );
        }
      }

      // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      // Update quantity selector's min/max/step attributes and cart quantity for the new variant
      const newQuantityInput = /** @type {HTMLInputElement | null} */ (
        html.querySelector('quantity-selector-component input[ref="quantityInput"]')
      );

      if (quantitySelector?.updateConstraints && newQuantityInput) {
        quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
        // REAL: Re-enable quantity after a sold-out initial variant becomes purchasable.
        quantitySelector.quantityInput.disabled = newQuantityInput.disabled;
        // Keep data-quantity-default attribute in sync with new variant's minimum quantity
        this.dataset.quantityDefault = newQuantityInput.min || '1';
      }

      const newQuantityRules = html.querySelector('.quantity-rules');
      const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

      const newPricePerItem = html.querySelector('price-per-item');
      const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

      if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
        // Store quantity value before morphing entire container
        const currentQuantityValue = quantitySelector.getValue?.();

        const newProductFormButtons = html.querySelector('.product-form-buttons');

        if (productFormButtons && newProductFormButtons) {
          morph(productFormButtons, newProductFormButtons);

          // Get the NEW quantity selector after morphing and update its constraints
          const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
            html.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
            // Temporarily set the old value so updateConstraints can snap it properly
            this.refs.quantitySelector.setValue(currentQuantityValue);
            // updateConstraints will snap to valid increment if needed
            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
            // Keep data-quantity-default attribute in sync with new variant's minimum quantity
            this.dataset.quantityDefault = newQuantityInputElement.min || '1';
          }
        }
      } else {
        // Update elements individually when layout isn't changing
        /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
        const morphTargets = [
          ['.quantity-label', quantityLabel, quantitySelector],
          ['.quantity-rules', quantityRules, this.refs.productFormButtons],
          ['price-per-item', pricePerItem, quantitySelectorWrapper],
        ];

        for (const [selector, currentElement, fallback] of morphTargets) {
          this.#morphOrUpdateElement(currentElement, html.querySelector(selector), fallback);
        }
      }

      // Morph volume pricing if it exists
      const currentVolumePricing = this.refs.volumePricing;
      const newVolumePricing = html.querySelector('volume-pricing');
      this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) return;

      // Fetch and update cart quantity for the new variant
      this.#refreshCart().then((cart) => this.#updateCartQuantity(cart));
    } catch (error) {
      if (generation === this.#variantChangeGeneration && this.isConnected) {
        if (error?.name !== 'AbortError') {
          console.warn('[product-form] Could not update the selected product option:', error);
        }
        this.refs.variantId.value = '';
        this.refs.addToCartButtonContainer?.disable();
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
        this.#showAddError(
          Theme.translations?.product_options_load_error || 'Product options could not be loaded. Try again.'
        );
      }
    } finally {
      // Only clear the flag if no newer variant selection has started
      if (generation === this.#variantChangeGeneration) {
        this.#variantChangeInProgress = false;

        // Drain any queued add-to-cart requests that accumulated during the variant change
        await this.#drainAddToCartQueue();
      }
    }
  };

  /**
   * Drains the add-to-cart queue accumulated while a variant change was in flight.
   *
   * Each queued add resolves against the selection and generation that were active when Add was
   * clicked. If no variant resolves, that queued add is aborted so a stale, empty, or maxed
   * variant id is never sent. The add-to-cart button is already disabled for unavailable
   * selections in #onProductSelect, so no further UI change is needed.
   */
  async #drainAddToCartQueue() {
    if (this.#addToCartQueue.length === 0) return;

    const queuedItems = [...this.#addToCartQueue];
    this.#addToCartQueue = [];

    /** @type {Array<{variantId: string, quantity: number}>} */
    const resolvedItems = [];
    for (const item of queuedItems) {
      const resolvedItem = await this.#resolveQueuedAddToCartItem(item);
      if (resolvedItem) {
        resolvedItems.push(resolvedItem);
      }
    }

    this.#processBatchAddToCart(resolvedItems);
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string, quantity: number} | null>}
   */
  async #resolveQueuedAddToCartItem(item) {
    const { variantId, quantityConstraints } = await this.#resolveQueuedVariant(item);
    if (!variantId) return null;

    const quantity = this.#normalizeQueuedQuantity(item.quantity, quantityConstraints);
    if (quantity === null) return null;

    return {
      variantId,
      quantity,
    };
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string | null, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveQueuedVariant(item) {
    /** @type {string | null} */
    let resolvedVariantId = null;
    /** @type {boolean | undefined} */
    let available;
    /** @type {QuantityConstraints | null} */
    let quantityConstraints = null;

    if (item.pendingVariantChange) {
      try {
        const result = /** @type {{detail?: {resource?: any, html?: Document | Element}}} */ (
          await item.pendingVariantChange
        );
        const resource = result?.detail?.resource;
        quantityConstraints = this.#getQuantityConstraintsFromHtml(result?.detail?.html);
        if (resource === null) {
          available = false;
        } else if (resource) {
          resolvedVariantId = resource.id != null ? String(resource.id) : null;
          available = resource.available !== false;
        }
      } catch {
        const resolvedVariant = await this.#resolveVariantFromUrl(
          item.variantResolutionUrl,
          item.variantPickerTagName
        ).catch(() => ({
          variantId: null,
          available: false,
          quantityConstraints: null,
        }));
        resolvedVariantId = resolvedVariant.variantId;
        available = resolvedVariant.available;
        quantityConstraints = resolvedVariant.quantityConstraints;
      }
    }

    const isLatestGeneration = item.generation === this.#variantChangeGeneration;
    const variantId = resolveVariantId({
      resolvedVariantId,
      intendedVariantId: item.intendedVariantId,
      hiddenInputValue: isLatestGeneration ? this.#getVariantIdInput() ?? null : null,
      available: available ?? (isLatestGeneration ? !this.#isAddToCartDisabled() : undefined),
    });

    return { variantId, quantityConstraints };
  }

  /**
   * @param {number} quantity
   * @param {QuantityConstraints | null} quantityConstraints
   * @returns {number | null}
   */
  #normalizeQueuedQuantity(quantity, quantityConstraints) {
    if (!quantityConstraints) return quantity;

    const min = parseIntOrDefault(quantityConstraints.min, 1);
    const max = parseIntOrDefault(quantityConstraints.max, null);
    const step = parseIntOrDefault(quantityConstraints.step, 1);
    const cartQuantity = parseIntOrDefault(quantityConstraints.cartQuantity, 0);
    const effectiveMax = max === null ? null : Math.max(max - cartQuantity, 0);
    if (effectiveMax !== null && effectiveMax < min) return null;

    const requestedQuantity = Math.max(quantity, min);
    let normalizedQuantity = min + Math.floor((requestedQuantity - min) / step) * step;

    if (effectiveMax !== null) {
      const maximumValidQuantity = min + Math.floor((effectiveMax - min) / step) * step;
      normalizedQuantity = Math.min(normalizedQuantity, maximumValidQuantity);
    }

    return Math.max(min, normalizedQuantity);
  }

  /**
   * @param {Document | Element | null | undefined} html
   * @returns {QuantityConstraints | null}
   */
  #getQuantityConstraintsFromHtml(html) {
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      html?.querySelector?.('quantity-selector-component input[ref="quantityInput"]') ?? null
    );
    if (!quantityInput) return null;

    return {
      min: quantityInput.min,
      max: quantityInput.max || null,
      step: quantityInput.step,
      cartQuantity: quantityInput.getAttribute('data-cart-quantity'),
    };
  }

  /**
   * Resolves a queued selection using the server-side section renderer when the original in-flight
   * request was aborted by a later variant selection.
   * @param {string | null} variantResolutionUrl
   * @param {string | null} variantPickerTagName
   * @returns {Promise<{variantId: string | null, available: boolean | undefined, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveVariantFromUrl(variantResolutionUrl, variantPickerTagName) {
    if (!variantResolutionUrl) return { variantId: null, available: undefined, quantityConstraints: null };

    const abortController = new AbortController();
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      abortController.abort();
    }, CART_REQUEST_TIMEOUT);

    try {
      const response = await fetch(variantResolutionUrl, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: abortController.signal,
      });
      if (!response.ok) return { variantId: null, available: false, quantityConstraints: null };

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const variantPicker = html.querySelector(
        variantPickerTagName || 'variant-picker, swatches-variant-picker-component'
      );
      if (!variantPicker) return { variantId: null, available: false, quantityConstraints: null };

      const quantityConstraints = this.#getQuantityConstraintsFromHtml(html);
      const textContent = variantPicker.querySelector('script[type="application/json"]')?.textContent?.trim();
      if (!textContent) return { variantId: null, available: false, quantityConstraints };

      const resource = JSON.parse(textContent);
      if (resource === null) return { variantId: null, available: false, quantityConstraints };
      if (
        typeof resource !== 'object' ||
        Array.isArray(resource) ||
        (typeof resource.id !== 'number' && typeof resource.id !== 'string') ||
        typeof resource.available !== 'boolean'
      ) {
        return { variantId: null, available: false, quantityConstraints };
      }

      return {
        variantId: resource.id != null ? String(resource.id) : null,
        available: resource.available,
        quantityConstraints,
      };
    } catch (error) {
      if (timedOut) throw new Error('Variant resolution request timed out');
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }

  /**
   * @returns {import('@theme/variant-picker').default | null}
   */
  #getVariantPicker() {
    const container = this.closest('product-card') ?? this.closest('dialog') ?? this.closest('.shopify-section');
    const pickers = /** @type {import('@theme/variant-picker').default[]} */ (
      Array.from(container?.querySelectorAll('variant-picker, swatches-variant-picker-component') ?? [])
    );
    const productId = this.dataset.productId;

    if (productId) {
      const matchingPicker = pickers.find((picker) => picker.dataset.productId === productId);
      if (matchingPicker) return matchingPicker;
    }

    return pickers.length === 1 ? pickers[0] ?? null : null;
  }

  /**
   * Whether the current selection's add-to-cart button is disabled (unavailable selection).
   * @returns {boolean}
   */
  #isAddToCartDisabled() {
    const containers = /** @type {NodeListOf<AddToCartComponent>} */ (this.querySelectorAll('add-to-cart-component'));
    return Array.from(containers).some((container) => container.refs.addToCartButton?.disabled);
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
